import { AlertTriangle, Check, EyeOff, Star, StarHalf } from 'lucide-react';
import { Fragment, type ReactNode, useEffect, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';

function formatApiError(err: unknown): string {
    if (err instanceof Error) {
        return err.message;
    }
    return String(err);
}

// Per-client severity override states for one tag. Maps 1:1 to the
// ConstraintLevel enum on the backend (libraries/python/
// tag_normalization/types.py:22). 'default' is the sentinel for "no
// per-client override — defer to the canonical/org rule's importance".
type Override = 'default' | 'soft' | 'preferred' | 'hard' | 'ignore';

const OVERRIDE_LABEL: Record<Override, string> = {
    default: 'Default',
    soft: 'Soft',
    preferred: 'Preferred',
    hard: 'Required',
    ignore: 'Ignore',
};

// Descriptions phrased from the caregiver-matching POV so it's clear
// who gets ranked / excluded. "Rank lower" alone read ambiguously
// (client? caregiver?) — these spell out the subject.
const OVERRIDE_DESCRIPTION: Record<Override, string> = {
    default: 'Use the org / canonical rule',
    soft: 'Slightly prefer caregivers who match',
    preferred: 'Prefer caregivers who match',
    hard: 'Only show caregivers who match',
    ignore: 'Skip this tag for this client',
};

// Variant + accent icon per override. `default` renders as the
// existing muted Badge so the page looks identical to today's UI
// before any admin has touched a tag. Non-default overrides show a
// small accent icon so the coordinator can scan the badge row and
// see at a glance which tags carry a per-client decision.
const BADGE_VARIANT: Record<
    Override,
    'secondary' | 'default' | 'destructive' | 'outline'
> = {
    default: 'secondary',
    soft: 'default',
    preferred: 'default',
    hard: 'destructive',
    ignore: 'outline',
};

const ACCENT_ICON: Record<Override, ReactNode> = {
    default: null,
    soft: <StarHalf className="h-3 w-3" />,
    preferred: <Star className="h-3 w-3" />,
    hard: <AlertTriangle className="h-3 w-3" />,
    ignore: <EyeOff className="h-3 w-3" />,
};

type Props = {
    readonly tag: string;
    readonly organizationId: string;
    readonly clientId: string;
    readonly initialOverride?: Override;
    // Resolved dial value (client > org > canonical) so the chip can
    // show the strictness that actually applies when there is no
    // per-client override.
    readonly effectiveImportance?: Override | null;
};

export function ClientTagOverrideChip({
    tag,
    organizationId,
    clientId,
    initialOverride = 'default',
    effectiveImportance = null,
}: Props) {
    const [override, setOverride] = useState<Override>(initialOverride);
    const [open, setOpen] = useState(false);

    // Sync local state when the parent re-renders with a different
    // initialOverride. Without this, useState only honors the prop on
    // mount, so a chip showing client A's tag would keep that state
    // if the modal stays mounted while the parent re-fetches and
    // gets fresh override data, or if the user switches clients
    // without the chip being remounted.
    useEffect(() => {
        setOverride(initialOverride);
    }, [initialOverride]);

    const apply = async (next: Override) => {
        setOpen(false);
        if (next === override) {
            return;
        }
        const previous = override;
        // Optimistic update — revert on error.
        setOverride(next);
        try {
            await getApiClient().put({
                url: '/organizations/{organization_id}/clients/{client_id}/tag-overrides',
                pathParams: {
                    organization_id: organizationId,
                    client_id: clientId,
                },
                body: { tag_key: tag, importance: next },
            });
            toast.success(
                next === 'default'
                    ? `Cleared override for ${tag}`
                    : `${tag} → ${OVERRIDE_LABEL[next]}`,
                { persistNotification: false },
            );
        } catch (err) {
            setOverride(previous);
            toast.error(
                `Failed to update override for ${tag}: ${formatApiError(err)}`,
                { persistNotification: false },
            );
        }
    };

    const accentIcon = ACCENT_ICON[override];

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild={true}>
                <button
                    type="button"
                    className="focus-visible:ring-ring rounded focus-visible:ring-2 focus-visible:outline-none"
                    title={
                        override === 'default'
                            ? 'Click to override matching severity for this client'
                            : OVERRIDE_LABEL[override]
                    }
                >
                    <Badge
                        variant={BADGE_VARIANT[override]}
                        className={cn(
                            'cursor-pointer gap-1 text-xs',
                            override !== 'default' && 'font-medium',
                        )}
                    >
                        {accentIcon}
                        {tag}
                        {override === 'default' &&
                        effectiveImportance &&
                        effectiveImportance !== 'default' ? (
                            <span className="text-muted-foreground font-normal">
                                · {OVERRIDE_LABEL[effectiveImportance]}
                            </span>
                        ) : null}
                    </Badge>
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
                    Override for this client
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(
                    ['default', 'hard', 'preferred', 'soft', 'ignore'] as const
                ).map((opt) => (
                    <DropdownMenuItem
                        key={opt}
                        onClick={() => apply(opt)}
                        className="flex flex-col items-start gap-0.5 py-2"
                    >
                        <div className="flex w-full items-center justify-between">
                            <span className="text-sm">
                                {OVERRIDE_LABEL[opt]}
                            </span>
                            {override === opt ? (
                                <Check className="h-3.5 w-3.5" />
                            ) : (
                                <Fragment />
                            )}
                        </div>
                        <span className="text-muted-foreground text-xs">
                            {opt === 'default' &&
                            effectiveImportance &&
                            effectiveImportance !== 'default'
                                ? `${OVERRIDE_DESCRIPTION[opt]} — currently ${OVERRIDE_LABEL[effectiveImportance]}`
                                : OVERRIDE_DESCRIPTION[opt]}
                        </span>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
